import React, { useEffect, useMemo, useState } from "react";
import CategoryFilterBar from "./CategoryFilterBar";
import PosProductCard from "./PosProductCard";
import ProductGrid from "./ProductGrid";
import ProductSearchInput from "./ProductSearchInput";
import VarietyGroupTabs from "./VarietyGroupTabs";
import useProductSearchSettings from "../hooks/useProductSearchSettings";
import { areAllPricesSoldOut } from "../utils/productAvailability";
import { filterProductSearchResults } from "../utils/productSearch";
import { ALL_VARIETY_GROUP_ID, filterVarietiesByGroup } from "../utils/varietyGroups";

const defaultResolveProductCategoryId = (product) => {
  if (Number.isInteger(product?.categoryId)) return product.categoryId;
  if (product?.categoryId) return product.categoryId;
  return product?.Category?.id ?? null;
};

const defaultResolveCategoryLabel = (category) =>
  category?.name || category?.label || String(category?.id ?? "");

const StockProductPickerPanel = ({
  products = [],
  categories = [],
  categoriesLoading = false,
  categoriesError = "",
  resolveProductCategoryId = defaultResolveProductCategoryId,
  resolveCategoryLabel = defaultResolveCategoryLabel,
  onSelectVariety,
  closeOnSelectVariety = false,
  getVarietyBadge,
  renderVarietyReference,
  renderProductAction,
  searchPlaceholder = "Buscar producto...",
  emptyStateLabel = "No hay productos disponibles.",
  wrapperClassName = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [modalProduct, setModalProduct] = useState(null);
  const [activeVarietyGroupId, setActiveVarietyGroupId] = useState(ALL_VARIETY_GROUP_ID);
  const { searchMode } = useProductSearchSettings();

  const inferredCategories = useMemo(() => {
    const byId = new Map();
    (products || []).forEach((product) => {
      const categoryId = resolveProductCategoryId(product);
      if (categoryId == null) return;
      const key = String(categoryId);
      if (byId.has(key)) return;
      const fallbackLabel =
        product?.Category?.name ||
        product?.categoryName ||
        `Categoria ${key}`;
      byId.set(key, { id: categoryId, label: fallbackLabel });
    });
    return Array.from(byId.values());
  }, [products, resolveProductCategoryId]);

  const displayCategories = categories.length ? categories : inferredCategories;

  const filteredProducts = useMemo(() => {
    const baseProducts = Array.isArray(products) ? products : [];
    if ((searchTerm || "").trim()) {
      return filterProductSearchResults(baseProducts, searchTerm, { searchMode });
    }
    if (selectedCategory !== "all") {
      return baseProducts.filter(
        (product) =>
          String(resolveProductCategoryId(product)) === String(selectedCategory)
      );
    }
    return baseProducts;
  }, [products, searchTerm, selectedCategory, resolveProductCategoryId, searchMode]);

  useEffect(() => {
    if (selectedCategory === "all") return;
    const stillExists = displayCategories.some(
      (category) => String(category?.id) === String(selectedCategory)
    );
    if (!stillExists) {
      setSelectedCategory("all");
    }
  }, [displayCategories, selectedCategory]);

  useEffect(() => {
    setActiveVarietyGroupId(ALL_VARIETY_GROUP_ID);
  }, [modalProduct?.id]);

  const groupedCategories = useMemo(() => {
    if (!displayCategories.length) {
      return [];
    }
    const scope =
      selectedCategory === "all"
        ? displayCategories
        : displayCategories.filter(
            (category) => String(category?.id) === String(selectedCategory)
          );
    return scope
      .map((category) => {
        const productsInCategory = filteredProducts
          .filter(
            (product) =>
              String(resolveProductCategoryId(product)) === String(category?.id)
          )
          .sort((a, b) =>
            String(a?.productName || "").localeCompare(
              String(b?.productName || "")
            )
          );
        return { category, products: productsInCategory };
      })
      .filter((entry) => entry.products.length > 0);
  }, [
    displayCategories,
    selectedCategory,
    filteredProducts,
    resolveProductCategoryId,
  ]);

  const productCategoryLabel = useMemo(() => {
    if (!modalProduct) return "-";
    const categoryId = resolveProductCategoryId(modalProduct);
    const category = displayCategories.find(
      (entry) => String(entry?.id) === String(categoryId)
    );
    return category ? resolveCategoryLabel(category) : "Sin categoria";
  }, [modalProduct, displayCategories, resolveCategoryLabel, resolveProductCategoryId]);

  const visibleModalVarieties = useMemo(() => {
    if (!modalProduct) return [];
    return filterVarietiesByGroup(
      modalProduct.Varieties || [],
      modalProduct.VarietyGroups,
      activeVarietyGroupId
    );
  }, [modalProduct, activeVarietyGroupId]);

  return (
    <div className={wrapperClassName}>
      <div className="sticky top-0 pt-2 z-10 bg-gray-100">
        <ProductSearchInput
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          products={products}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="w-full mb-6 p-3 border border-gray-300 rounded-lg shadow focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <CategoryFilterBar
        categories={displayCategories}
        selectedCategory={selectedCategory}
        onSelect={setSelectedCategory}
        includeAll
        allItem={{ id: "all", label: "Todos", initial: "T" }}
        itemsWrapperClassName="mb-6"
        loading={categoriesLoading}
        error={categoriesError}
      />

      <div className="space-y-8">
        {groupedCategories.length > 0 ? (
          groupedCategories.map((entry) => (
            <div key={String(entry.category?.id)}>
              <div className="flex items-center gap-4 my-6">
                <div className="flex-grow border-t border-gray-300"></div>
                <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                  {resolveCategoryLabel(entry.category)}
                </h2>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>

              <ProductGrid>
                {entry.products.map((product) => (
                  <PosProductCard
                    key={product.id}
                    productName={product.productName}
                    imageSrc={product.productImage || "https://placehold.co/200"}
                    onClick={() => setModalProduct(product)}
                  >
                    {product.metricUnit && (
                      <span className="text-[11px] text-gray-500">{product.metricUnit}</span>
                    )}
                  </PosProductCard>
                ))}
              </ProductGrid>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-600">{emptyStateLabel}</p>
        )}
      </div>

      {modalProduct && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex items-start justify-center z-40 px-4 py-6 overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Producto
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  {modalProduct.productName}
                </h2>
                <p className="text-sm text-slate-600">
                  Categoria: {productCategoryLabel}
                </p>
              </div>
              <button
                onClick={() => setModalProduct(null)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <img
                  src={modalProduct.productImage || "https://placehold.co/300"}
                  alt={modalProduct.productName}
                  className="w-28 h-28 object-cover rounded-xl border border-slate-200"
                />
                <div className="flex-1 space-y-1">
                  <p className="text-sm text-slate-600">Vista general</p>
                  <p className="text-base font-semibold text-slate-900">
                    Variedades disponibles
                  </p>
                </div>
                {typeof renderProductAction === "function"
                  ? renderProductAction({
                      product: modalProduct,
                      closeProductModal: () => setModalProduct(null),
                    })
                  : null}
              </div>

              <VarietyGroupTabs
                groups={modalProduct.VarietyGroups}
                activeGroupId={activeVarietyGroupId}
                onSelect={setActiveVarietyGroupId}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {visibleModalVarieties.map((variety) => {
                  const sellablePrices = (variety.VarietyPrices || []).filter(
                    (price) => price.price !== null && price.price !== undefined
                  );
                  const isVarietySoldOut = areAllPricesSoldOut(sellablePrices);
                  const badge =
                    typeof getVarietyBadge === "function"
                      ? getVarietyBadge(variety, {
                          product: modalProduct,
                          sellablePrices,
                          isVarietySoldOut,
                        })
                      : null;
                  const badgeLabel = badge?.label || "Faltante";
                  const badgeClassName =
                    badge?.badgeClassName || "bg-amber-100 text-amber-700";
                  const cardClassName =
                    badge?.cardClassName || "bg-amber-50/70 border-amber-300";
                  const referenceContent =
                    typeof renderVarietyReference === "function"
                      ? renderVarietyReference(variety, {
                          product: modalProduct,
                          sellablePrices,
                          isVarietySoldOut,
                        })
                      : null;

                  return (
                    <button
                      key={variety.id}
                      type="button"
                      className={`relative text-left rounded-xl border p-4 hover:shadow-md transition ${cardClassName}`}
                      onClick={() => {
                        if (!onSelectVariety) return;
                        onSelectVariety(variety, modalProduct);
                        if (closeOnSelectVariety) setModalProduct(null);
                      }}
                    >
                      <span
                        className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${badgeClassName}`}
                      >
                        {badgeLabel}
                      </span>
                      <div className="flex items-center gap-3">
                        <img
                          src={variety.varietyImage || "https://placehold.co/200"}
                          alt={variety.varietyName}
                          className="w-14 h-14 object-cover rounded-lg border border-slate-200"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-800">
                            {variety.varietyName}
                          </p>
                          {referenceContent || (
                            <p className="text-slate-600 text-xs mt-1">
                              {isVarietySoldOut ? "AGOTADO" : "Seleccionar variedad"}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {visibleModalVarieties.length === 0 && (
                  <p className="text-sm text-slate-500">No hay variedades para este grupo.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockProductPickerPanel;
